import * as React from 'react';
import getMailItemByImmutableIdOrItemId from './getMailItemByImmutableIdOrItemId';
import getUnreadRepliesFromConversationNodes from './getUnreadRepliesFromConversationNodes';
import isFeatureEnabled from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import type Message from 'owa-service/lib/contract/Message';
import popoutMailItemFromPayload from './popoutMailItemFromPayload';
import type { AssetCommonViewProps } from 'owa-timestream-asset-sdk-types';
import {
    AssetCommandDescriptors,
    useAssetCommands,
    usePatchPayload,
    usePatchPartialPayload,
} from 'owa-timestream-asset-sdk';
import { getAttachmentPreviews } from '../selectors/getAttachmentPreviews';
import { getUnreadReplies } from '../selectors/getUnreadReplies';
import { INVOICE_ENTITY_NAMES_MAP_VALUE } from 'owa-txp-common/lib/helpers/TxpCommonConstants';
import type { MailItemPayload, UnreadRepliesProps } from '../schema';
import { toJS } from 'mobx';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { useIsMounted } from 'owa-react-hooks/lib/useIsMounted';
import { createRowNotificationSubscription } from '../notifications/rowNotificationsStrategy';
import { logSpacesDatapoint } from 'owa-timestream-logging';

export const UNREAD_REPLIES_COUNT: number = 3;

export default function useMailItemViewState({
    isDragPreview,
    payload,
}: AssetCommonViewProps<MailItemPayload>) {
    const { immutableId, item = {}, isTXPView = true } = payload;
    const itemId = item.ItemId?.Id;
    const attachmentPreviews =
        itemId && item.HasAttachments && item.ConversationId
            ? getAttachmentPreviews(item.ConversationId) || []
            : [];
    const unreadReplies =
        isFeatureEnabled('outlookSpaces-mailThread') && item.ConversationId
            ? getUnreadReplies(item.ConversationId) || []
            : [];

    const relatedReplies: Message[] =
        itemId && unreadReplies && unreadReplies.length > 0
            ? getUnreadRepliesFromConversationNodes(itemId, unreadReplies)
            : [];

    const patchPayload = usePatchPayload<MailItemPayload>();
    const [notFound, setNotFound] = useBooleanState(false);
    const isMounted = useIsMounted();
    const refreshMailMessage = React.useCallback(
        function refreshMailMessage() {
            getMailItemByImmutableIdOrItemId(immutableId, itemId).then(
                newItem => {
                    if (newItem) {
                        const oldData = getCleanMessageData(item);
                        const newData = getCleanMessageData(newItem);
                        const shouldPatch = oldData != newData;
                        if (shouldPatch) {
                            patchPayload({ item: newItem, immutableId, isTXPView });
                        }
                    } else {
                        if (isMounted.current) {
                            setNotFound();
                        }
                    }
                },
                () => {
                    // Nothing to do if we fail to refresh, just show the item we have cached.
                    // Do we want to support retry, maybe with an asset command? One can always close & re-open the project.
                }
            );
        },
        [immutableId, itemId, item]
    );

    React.useEffect(() => {
        if (!isDragPreview) {
            refreshMailMessage();
            item?.ParentFolderId?.Id && createRowNotificationSubscription(item?.ParentFolderId?.Id);
            logSpacesDatapoint('OUTLOOKSPACES_MAIL_UNREAD_REPLIES_GENERATED', {
                unreadRepliesCount: relatedReplies.length.toString(),
            });
        }
    }, [isDragPreview, unreadReplies]);

    const patchPartialPayload = usePatchPartialPayload<MailItemPayload>();
    const switchTXPView = React.useCallback(() => {
        logSpacesDatapoint('OUTLOOKSPACES_MAIL_TXP_VIEW_SWITCH', {
            switchToTXPView: !payload.isTXPView,
        });
        patchPartialPayload({ isTXPView: !payload.isTXPView });
    }, [payload.isTXPView]);

    const openReadingPane = React.useCallback(() => {
        popoutMailItemFromPayload(payload);
    }, [payload.immutableId, payload.item?.ItemId?.Id]);

    let txpButtonLabel = payload.isTXPView
        ? AssetCommandDescriptors.switchOriginal
        : AssetCommandDescriptors.switchTXP;

    let commands = [
        {
            ...AssetCommandDescriptors.openInNewWindow,
            onInvoke: openReadingPane,
        },
        {
            ...txpButtonLabel,
            onInvoke: switchTXPView,
            hidden: !(
                payload.item?.EntityNamesMap &&
                payload.item?.EntityNamesMap != INVOICE_ENTITY_NAMES_MAP_VALUE &&
                isFeatureEnabled('outlookSpaces-txpView')
            ),
        },
    ];

    useAssetCommands(commands, [openReadingPane, switchTXPView]);

    const unreadRepliesProps: UnreadRepliesProps = {
        unreadReplies: relatedReplies,
        unreadRepliesCount: relatedReplies.length,
    };

    return {
        attachmentPreviews,
        item,
        notFound,
        openReadingPane,
        unreadRepliesProps,
    };
}

export function getCleanMessageData(message: Message) {
    const cleanMessage = toJS(message);
    if (cleanMessage.ItemId) {
        delete cleanMessage.ItemId.ChangeKey;
    }
    return JSON.stringify(cleanMessage);
}
